import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { fetchEventSource } from 'services/http';
import { EventStreamContentType } from '@microsoft/fetch-event-source';
import { connect } from 'react-redux';

import BaseMessage from '../BaseMessage';
import AnswerMessage, { FooterLocation } from 'components/message/AnswerMessage';
import InfoMessage from 'components/message/InfoMessage';
import HiddenMessage from 'components/message/HiddenMessage';
import ChatMessageMarkdownHtml from 'components/message/ChatMessageMarkdownHtml/ChatMessageMarkdownHtml';
import LoadingIcon from 'components/loaders/LoadingIcon';

import { fetchPartialAnswers } from 'store/modules/chat-messages';

import styles from './multi-step-message.scss';

import Icon from 'components/icons/Icon';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import MultiStepMessageFooter from '../MultiStepMessageFooter';
import { fetchPills } from 'store/modules/pills';

const MultiStepMessage = ({
  answerId,
  questionId,
  childAnswers,
  addChildAnswer,
  useScrollBuffer,
  scrollTo,
  addToBoardCallback,
  disableAnswerFootersAndTrailers,
  customLastFooter,
  isLlmDataAccessAllowed,
  currentKgId,
  dispatch,
  t
}) => {
  // true as soon as we are done streaming the message (either successfully or ended with an error)
  const [isStreamingDone, setIsStreamingDone] = useState(false);
  const [hoveredChildMessageId, setHoveredChildMessageId] = useState(null);
  // tracks whether there was any await-result tool call during the conversation
  const [hasAwaitResultCallAtEnd, setHasAwaitResultCallAtEnd] = useState(false);

  const addChildAnswerInternal = event => {
    return async dispatch => {
      if (!event) return;

      if (event.event === 'answer') {
        const parsedAnswer = JSON.parse(event.data);
        fetchPartialAnswers(parsedAnswer, dispatch);
      }

      await addChildAnswer(event);
    };
  };

  // event for the markdown message that is currently under construction and not flushed to the redux state yet
  const [markdownUnderConstruction, setMarkdownUnderConstruction] = useState(null);
  // need this ref in addition to the state in order to have immediate access to the most recent markdown under
  //  construction even before re-rendering (state updates and re-renderings can be delayed)
  const markdownUnderConstructionRef = useRef(null);

  // Waiting message shown while the system is waiting for something (e.g., waiting for database results). If this
  //  is null, we are not waiting for anything.
  const [waitingMessage, setWaitingMessage] = useState(null);

  const countOfAnswerMessages = childAnswers?.filter(msg => msg.type === 'answer' && !msg.answer?.isError).length || 0;

  // the footer to be rendered for a message
  const getCustomFooter = isLastAndDone => {
    // if this is the last message and streaming is completed, we show a custom footer
    if (isLastAndDone) {
      const shouldShowSummarizeFooter =
        !disableAnswerFootersAndTrailers &&
        isLlmDataAccessAllowed &&
        currentKgId &&
        countOfAnswerMessages >= 2 &&
        !hasAwaitResultCallAtEnd;
      if (shouldShowSummarizeFooter) {
        return (
          <MultiStepMessageFooter
            answerId={answerId}
            questionId={questionId}
            isLlmDataAccessAllowed={isLlmDataAccessAllowed}
          />
        );
      } else {
        // show the custom last footer if there is no summarize footer (this deliberately does not respect the
        //  disableAnswerFootersAndTrailers and is used for discovery)
        return customLastFooter;
      }
    } else {
      // no custom footer otherwise
      return undefined;
    }
  };

  const renderedMessages = childAnswers?.map((msg, index) => {
    // this is the last message if it is the last one in childAnswers and there is no markdown under construction or waiting message
    const isLast = index === childAnswers.length - 1 && !markdownUnderConstruction && !waitingMessage;
    const isFirst = index === 0;

    // the message footer with retry and feedback controls should only be shown on the last message and only once
    //  streaming is done
    const isLastAndDone = isLast && isStreamingDone;
    const isMiddle = !isFirst && !isLast;
    const isSingle = isFirst && isLast;

    // Determine the position string
    let position = null;
    if (isSingle) position = 'single';
    else if (isFirst) position = 'first';
    else if (isMiddle) position = 'middle';
    else if (isLast) position = 'last';

    const childMessageId = msg.id;
    if (msg.type === 'markdown') {
      const content = (
        <div className={styles.wrapper}>
          <ChatMessageMarkdownHtml html={msg.text} scrollTo={scrollTo} />
        </div>
      );

      return (
        <div data-test="multiStepTextMessageBlock" data-question-id={questionId} data-answer-id={answerId} key={index}>
          <BaseMessage
            content={content}
            messageId={childMessageId}
            questionId={questionId}
            answerId={answerId}
            enableTrailer={isLastAndDone}
            enableRetry={!disableAnswerFootersAndTrailers && isLastAndDone}
            disableHideMessageButton
            enableFeedback={!disableAnswerFootersAndTrailers && isLastAndDone}
            feedback={msg.feedback}
            showVeezooIcon={isFirst}
            customFooter={getCustomFooter(isLastAndDone)}
            multiStepPosition={position}
          />
        </div>
      );
    } else if (msg.type === 'answer') {
      const answer = msg.answer;
      if (answer.isError) {
        return (
          <div key={index}>
            <InfoMessage
              message={answer}
              disableRetry={!isLastAndDone || disableAnswerFootersAndTrailers}
              disableTrailer={!isLastAndDone || disableAnswerFootersAndTrailers}
              multiStepPosition={position}
              t={t}
            />
          </div>
        );
      } else {
        const customFooter = getCustomFooter(isLastAndDone);

        const isHovered = hoveredChildMessageId === childMessageId;
        // show the standard footer (including follow-up, add to board, etc.) if the answer is howeverd, except if
        //  footers are disabled (used for discovery)
        const enableStandardFooter = !disableAnswerFootersAndTrailers && isHovered;

        return (
          <div
            key={index}
            onMouseEnter={() => setHoveredChildMessageId(childMessageId)}
            onMouseLeave={() => setHoveredChildMessageId(null)}
          >
            <AnswerMessage
              {...answer}
              disableTeach
              disableRetry={!isLastAndDone || disableAnswerFootersAndTrailers}
              disableTrailer={!isLastAndDone || disableAnswerFootersAndTrailers}
              id={childMessageId}
              addToBoardCallback={addToBoardCallback}
              disableHideMessageButton
              disableVeezooIcon={!isFirst}
              multiStepPosition={position}
              contentClassName={styles.multiStepAnswerContent}
              standardFooterLocation={enableStandardFooter ? FooterLocation.INNER : FooterLocation.DISABLED}
              customFooter={customFooter}
              customFooterLocation={FooterLocation.OUTER}
            />
          </div>
        );
      }
    } else if (msg.type === 'USER_HIDDEN_MESSAGE') {
      const hiddenMessageIds = { answerMessageIds: [childMessageId] };
      return (
        <div key={index}>
          <HiddenMessage messageId={childMessageId} hiddenMessageIds={hiddenMessageIds} />
        </div>
      );
    } else if (msg.type === 'error' || msg.type === 'warning') {
      const isWarning = msg.type === 'warning';
      const content = (
        <div className={styles.wrapper}>
          {isWarning ? (
            <Icon data-test="icon-warning" type="icon-warning" />
          ) : (
            <ErrorRoundedIcon style={{ fontSize: '2em', color: 'var(--error-red)' }} />
          )}
          <span className={styles.errorMessage}>{msg.text}</span>
        </div>
      );
      return (
        <div key={index}>
          <BaseMessage
            content={content}
            messageId={childMessageId}
            questionId={questionId}
            enableTrailer={!disableAnswerFootersAndTrailers && isLastAndDone}
            enableRetry={!disableAnswerFootersAndTrailers && isLastAndDone}
            enableFeedback={false}
            showVeezooIcon={isFirst}
            multiStepPosition={position}
            customFooter={getCustomFooter(isLastAndDone)}
          />
        </div>
      );
    }
    return null;
  });

  // move the markdown that is currently under construction (if any) to the redux state
  const flushCompletedMarkdown = async () => {
    if (markdownUnderConstructionRef.current) {
      await dispatch(addChildAnswerInternal(markdownUnderConstructionRef.current));

      // clear the ref immediately
      markdownUnderConstructionRef.current = null;
      // trigger re-render (this may be delayed)
      setMarkdownUnderConstruction(null);
    }
  };

  // updates the markdown under construction on the local state, without updating the redux state
  //  (note that markdown events are cumulative, so we can just replace any existing markdown under construction)
  const handleMarkdown = event => {
    // update the ref immediately
    markdownUnderConstructionRef.current = event;
    // trigger re-render (this may be delayed)
    setMarkdownUnderConstruction(event);
  };

  // Promise used for synchronization: this models an implicit queue that guarantees sequential processing of the
  //  incoming events, even if they arrive in bursts. This pomise holds the end of the queue.
  const processingQueueRef = useRef(Promise.resolve());

  // Whether the stream stopped with an error
  const isStreamStoppedWithErrorRef = useRef(false);

  // Handles errors while opening the stream or arriving mid-stream
  const handleError = async (err, isMidStream) => {
    console.error(`Error while processing stream: ${err.message}`);

    // remember that we got an error
    isStreamStoppedWithErrorRef.current = true;

    // flush any markdown under construction to the redux state
    await flushCompletedMarkdown();

    // if the error is mid-stream, we already added a custom error message, otherwise we add a generic error
    if (!isMidStream) {
      await dispatch(addChildAnswerInternal({ event: 'error', data: t('error') }));
    }

    // NOTE: we don't fetch pills upon errors
    setIsStreamingDone(true);
  };

  // Processes the next event in the queue
  const handleNextEvent = async event => {
    if (event.event === 'wait') {
      setWaitingMessage(event.data);
    } else if (event.event === 'metadata') {
      // handle metadata event, just to track await-result presence currently
      // should always come at the end
      try {
        const metadata = JSON.parse(event.data);
        if (metadata.hasAwaitResultCallAtEnd !== undefined) {
          setHasAwaitResultCallAtEnd(metadata.hasAwaitResultCallAtEnd);
        }
      } catch (e) {
        console.warn('Failed to parse metadata event:', e);
      }
    } else {
      // clear the waiting message as the system is no longer waiting
      setWaitingMessage(null);

      if (event.event === 'markdown') {
        handleMarkdown(event);
      } else {
        // at this point, we have either an 'answer', 'error', or 'warning' event

        // the markdown under construction (if any) has been completed, so we flush it
        await flushCompletedMarkdown();

        // after flush is complete, we can append the new answer
        await dispatch(addChildAnswerInternal(event));

        if (event.event === 'error') {
          const errorMessage = `Received error in stream`;
          const error = new Error(errorMessage);

          await handleError(error, true);

          // throw an error to stop processing of further events
          throw error;
        }
      }
    }
  };

  useEffect(() => {
    // Below, we handle several kinds of errors as follows:
    //  - Errors when opening the stream (HTTP status code other than 200): throw an error inside onopen,
    //    which is handled by onerror
    //  - Errors arriving mid-stream (receive event of type 'error'): handled in handleNextEvent, which calls handleError
    //  - General network errors: directly handled by onerror
    fetchEventSource(
      `/answers/multi-step-stream/${answerId}`,
      {
        headers: {
          // X-Requested-With needs to be set to something, otherwise the Play server in the backend rejects the request
          'X-Requested-With': 'XMLHttpRequest'
        },

        // do not stop the SSE stream if the browser is hidden (see https://github.com/Azure/fetch-event-source/issues/36)
        openWhenHidden: true,

        // check the response code when the stream is opened
        async onopen(response) {
          if (!response.ok || response.headers.get('content-type') !== EventStreamContentType) {
            // there was an error when fetching the stream; throw an exception to stop processing (will be caught by onerror)
            const errorMessage = `Failed to get stream`;
            throw new Error(errorMessage);
          }
        },

        // process incoming events
        async onmessage(event) {
          // ignore all other events (e.g., pings)
          const relevantEvents = ['markdown', 'answer', 'wait', 'warning', 'error', 'metadata'];
          if (!relevantEvents.includes(event?.event)) return;

          // Put the event into the queue such that it is processed in order
          //  (onmessage is asynchronous and can be called multiple times in parallel if events arrive in bursts)
          // NOTE: Any errors thrown within handleNextEvent are inside a promise and will not be caught by onerror below
          processingQueueRef.current = processingQueueRef.current.then(() => handleNextEvent(event));
        },

        // need to define an onclose event to make sure the stream is closed once the server closes the HTTP connection
        async onclose() {
          // only apply this logic in case the stream completes successfully (onclose is also called after onerror)
          if (!isStreamStoppedWithErrorRef.current) {
            // the markdown under construction (if any) has been completed, so we flush it
            await flushCompletedMarkdown();

            setIsStreamingDone(true);
            dispatch(fetchPills(false));
          }
        },

        // NOTE: onerror needs to be synchronous for the re-try suppression (see "throw err;" below) to work
        onerror(err) {
          // "fire and forget" the error handling (no need to wait for it here)
          handleError(err, false);

          // re-throw error here to make sure the stream is closed (and not re-tried) upon errors
          throw err;
        }
      },
      { addKgId: true, addSessionId: true }
    );
  }, []);

  const renderWaitingMessage = message => {
    return (
      <div className={styles.waitingMessage}>
        <LoadingIcon size={12} color="var(--normal-gray)" /> {message}
      </div>
    );
  };

  // Returns the message currently under construction (including the markdown under construction and the waiting message, if any).
  const getUnderConstructionMessage = isFirst => {
    const content = (
      <div className={styles.wrapper}>
        {markdownUnderConstruction && (
          <ChatMessageMarkdownHtml
            html={markdownUnderConstruction.data}
            scrollTo={scrollTo}
            withCursor={!waitingMessage}
          />
        )}
        {waitingMessage && renderWaitingMessage(waitingMessage)}
      </div>
    );
    // It's either the first and therefore single answer or the last answer
    let position = isFirst ? 'single' : 'last';
    return (
      <div data-question-id={questionId} data-answer-id={answerId}>
        <BaseMessage
          content={content}
          enableTrailer={false}
          enableRetry={false}
          disableHideMessageButton={true}
          enableFeedback={false}
          showVeezooIcon={isFirst}
          multiStepPosition={position}
        />
      </div>
    );
  };

  const hasAnswers = childAnswers?.length >= 1;
  const isWaitingForContent = !isStreamingDone && !markdownUnderConstruction && !hasAnswers;

  return (
    // Add scroll buffer if desired (when this is the last message in the chat container)
    // This provides space to show the streamed answer
    <div
      className={useScrollBuffer ? styles.containerScrollBuffer : undefined}
      data-test="multiStepMessageWrapper"
      data-question-id={questionId}
    >
      {renderedMessages}
      {isWaitingForContent && (
        <BaseMessage
          content={renderWaitingMessage(t('thinking'))}
          enableTrailer={false}
          enableRetry={false}
          disableHideMessageButton={true}
          enableFeedback={false}
        />
      )}
      {(markdownUnderConstruction || waitingMessage) && getUnderConstructionMessage(!hasAnswers)}
      {isStreamingDone && (
        // add an invisible marker once streaming completes, so we can detect the end of the stream in end2end tests
        <div id="streaming-completed-marker" data-answer-id={answerId} style={{ display: 'none' }}></div>
      )}
    </div>
  );
};

const mapStateToProps = state => {
  const currentKg = state.knowledgeGraph; // Use state.knowledgeGraph directly
  return {
    // Ensure to handle cases where currentKg might be null initially
    isLlmDataAccessAllowed: currentKg?.isLlmDataAccessAllowed || false,
    currentKgId: currentKg?.id || null
  };
};

export default withTranslation('veezoo')(connect(mapStateToProps)(MultiStepMessage));

MultiStepMessage.propTypes = {
  answerId: PropTypes.string,
  questionId: PropTypes.string,
  childAnswers: PropTypes.array,
  addChildAnswer: PropTypes.func,
  useScrollBuffer: PropTypes.bool,
  scrollTo: PropTypes.func,
  addToBoardCallback: PropTypes.func,
  disableAnswerFootersAndTrailers: PropTypes.bool,
  customLastFooter: PropTypes.node,
  isLlmDataAccessAllowed: PropTypes.bool,
  currentKgId: PropTypes.string,
  dispatch: PropTypes.func,
  t: PropTypes.func
};
